import CartApi from '@apis/cart/CartApi';
import cartQueries from '@apis/queries/cartQueries';
import shopQueries from '@apis/queries/shopQueries';
import useCart from '@hooks/cart/useCart';
import useCurrency from '@hooks/item/useCurrency';
import useLoading from '@hooks/ui/useLoading';
import useToast from '@hooks/ui/useToast';
import useAppCustom from '@hooks/useAppCustom';
import useProjectId from '@hooks/useProjectId';
import { AddToCartRequest } from '@models/cart';
import { ErrorResponse } from '@models/error';
import useCalculatedOrder from '@store/item/hooks/useCalculatedOrder';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orderLineToGtmFormat, pushEcommerceEvent } from '@utils/gtm';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

function useAddCart(shopId: string) {
    const { t } = useTranslation('item');
    const { orderLines } = useCalculatedOrder();
    const projectId = useProjectId();
    const toast = useToast();
    const { data: shop } = useQuery(shopQueries.detail(shopId));
    const { cart } = useCart(shopId);
    const queryClient = useQueryClient();
    const { startLoading, stopLoading } = useLoading();
    const currency = useCurrency();
    const { mutate } = useMutation({
        mutationFn: (request: AddToCartRequest) => CartApi.addToCart(shopId, request),
        onMutate() {
            startLoading();
        },
        onSuccess() {
            pushEcommerceEvent('add_to_cart', {
                value: orderLines[0].shopItemId,
                currency,
                items: [orderLineToGtmFormat({ orderLine: orderLines[0], shop })],
            });
            queryClient.invalidateQueries({ queryKey: cartQueries._def, type: 'all' });
            toast.success(t('addCart.success'));
        },
        onError(e: AxiosError<ErrorResponse>) {
            console.error(e);
            toast.error(e.response?.data?.message || t('addCart.error'));
        },
        onSettled() {
            stopLoading();
        },
    });
    const { itemMaxQuantity } = useAppCustom().item;

    const handleAddToCart = () => {
        if (orderLines.length !== 1) {
            toast.error(t('addCart.invalidAccess'));
            return;
        }
        const {
            quantity, defaultOptions, personalOptions, shopItemId,
        } = orderLines[0];
        // 최대 개수 확인
        const currentQty = cart?.items
            .find((item) => item.itemId === shopItemId)
            ?.qty || 0;
        if (currentQty + quantity > itemMaxQuantity) {
            toast.error(t('addCart.maxQuantityError', { max: itemMaxQuantity, current: currentQty }));
            return;
        }

        const options = [...defaultOptions, ...personalOptions]
            .map((optionGroup) => optionGroup.options).flat();
        const optionIds = [...new Set(options.map((option) => option.shopOptionId))];

        const newOptions = optionIds.map((optionId) => {
            const matchedOptions = options.filter((option) => option.shopOptionId === optionId);
            const totalQuantity = matchedOptions.reduce((sum, option) => sum + option.quantity, 0);
            return {
                optionId, quantity: totalQuantity,
            };
        });
        const request: AddToCartRequest = {
            itemId: shopItemId,
            quantity,
            projectId,
            cartId: cart?.cartId,
            options: newOptions,
        };
        console.log({ request });
        mutate(request);
    };
    return {
        handleAddToCart,
    };
}

export default useAddCart;
